import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Response interceptor for error handling
api.interceptors.response.use(
  response => response,
  error => {
    if (error.response?.status === 401) {
      // Redirect to login if unauthorized
      if (!window.location.pathname.includes('/login') && !window.location.pathname.includes('/register')) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Helper to format API error messages
export const formatApiError = (error) => {
  const detail = error.response?.data?.detail;
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map(e => e?.msg || JSON.stringify(e)).filter(Boolean).join(' ');
  }
  if (detail?.msg) return detail.msg;
  return String(detail);
};

// Auth
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  register: (email, password, name) => api.post('/auth/register', { email, password, name }),
  logout: () => api.post('/auth/logout'),
  me: () => api.get('/auth/me'),
  exchangeSession: (sessionId) => api.post('/auth/session', { session_id: sessionId }),
  roleSignup: (data) => api.post('/auth/role-signup', data),
  changeEmail: (data) => api.post('/auth/change-email', data),
  changePassword: (data) => api.post('/auth/change-password', data),
  linkGoogle: (data) => api.post('/auth/link-google', data),
  unlinkGoogle: () => api.post('/auth/unlink-google'),
  deleteAccount: () => api.delete('/users/me'),
};

// Users
export const usersAPI = {
  getProfile: (userId) => api.get(`/users/${userId}`),
  updateProfile: (data) => api.put('/users/profile', data),
  listUsers: (params) => api.get('/users', { params }),
  extractWorkHistory: (file) => { const fd = new FormData(); fd.append('file', file); return api.post('/users/resume/extract-work-history', fd, { headers: { 'Content-Type': 'multipart/form-data' } }); }
};

// Clips
export const clipsAPI = {
  create: (file, caption) => { const fd = new FormData(); fd.append('file', file); if (caption) fd.append('caption', caption); return api.post('/clips', fd, { headers: { 'Content-Type': 'multipart/form-data' } }); },
  list: (params) => api.get('/clips', { params }),
  feed: (params) => api.get('/clips/feed', { params }),
  view: (clipId) => api.post(`/clips/${clipId}/view`),
  delete: (clipId) => api.delete(`/clips/${clipId}`)
};

// Curated Content
export const curatedAPI = {
  list: (params) => api.get('/curated', { params }),
  fetchRSS: () => api.post('/admin/curated/fetch-rss'),
  getSources: () => api.get('/admin/curated/sources'),
  manageSource: (data) => api.post('/admin/curated/sources', data),
  tiktokEmbed: (url) => api.get('/embed/tiktok', { params: { url } }),
  fetchTikTok: (data) => api.post('/admin/curated/fetch-tiktok', data),
  addTikTokSingle: (data) => api.post('/admin/curated/add-tiktok-single', data),
  editContent: (contentId, data) => api.post(`/admin/curated/${contentId}/edit`, data),
  getCreators: () => api.get('/admin/curated/tiktok-creators'),
  manageCreator: (data) => api.post('/admin/curated/tiktok-creators', data),
  getThemes: () => api.get('/admin/curated/themes'),
  manageTheme: (data) => api.post('/admin/curated/themes', data),
  migrateTikTokEmbeds: () => api.post('/admin/curated/migrate-tiktok-embeds'),
  refreshCreatorVideos: (perCreator = 3) => api.post('/admin/curated/refresh-creator-videos', { per_creator: perCreator }),
  refreshYouTubeCreators: (perCreator = 3) => api.post('/admin/curated/refresh-youtube-creators', { per_creator: perCreator }),
  dedupe: () => api.post('/admin/curated/dedupe'),
  backfillImages: () => api.post('/admin/curated/backfill-images'),
  like: (contentId) => api.post(`/curated/${contentId}/like`),
  react: (contentId, type) => api.post(`/curated/${contentId}/react`, { type }),
  likers: (contentId, params) => api.get(`/curated/${contentId}/likes`, { params }),
  comment: (contentId, content) => api.post(`/curated/${contentId}/comment`, { content }),
  reactComment: (contentId, commentId, type) => api.post(`/curated/${contentId}/comment/${commentId}/react`, { type }),
  commentLikers: (contentId, commentId, params) => api.get(`/curated/${contentId}/comment/${commentId}/likes`, { params }),
};

// External Jobs
export const externalJobsAPI = {
  list: (params) => api.get('/jobs/external', { params }),
  fetch: (data) => api.post('/admin/jobs/fetch-external', data || {})
};

// Institutions
export const institutionsAPI = {
  list: () => api.get('/institutions'),
  get: (id) => api.get(`/institutions/${id}`)
};

// Categories
export const categoriesAPI = {
  list: () => api.get('/categories')
};

// Service Providers
export const providersAPI = {
  list: (params) => api.get('/providers', { params }),
  get: (id) => api.get(`/providers/${id}`),
  create: (data) => api.post('/providers', data),
  getCategories: () => api.get('/providers/categories'),
  premiumStatus: () => api.get('/providers/premium/status'),
  premiumSubscribe: (data) => api.post('/providers/premium/subscribe', data),
  premiumAnalytics: () => api.get('/providers/premium/analytics'),
  update: (id, data) => api.put(`/providers/${id}`, data),
  delete: (id) => api.delete(`/providers/${id}`),
};

// Jobs
export const jobsAPI = {
  list: (params) => api.get('/jobs', { params }),
  get: (id) => api.get(`/jobs/${id}`),
  create: (data) => api.post('/jobs', data),
  parse: (text) => api.post('/jobs/parse', { text }),
  update: (id, data) => api.put(`/jobs/${id}`, data),
  delete: (id) => api.delete(`/jobs/${id}`),
  apply: (jobId, data) => api.post(`/jobs/${jobId}/apply`, data),
  applicationStatus: (jobId) => api.get(`/jobs/${jobId}/application-status`),
  myApplications: () => api.get('/me/applications'),
  myPostedJobs: () => api.get('/me/posted-jobs'),
  getJobApplications: (jobId) => api.get(`/jobs/${jobId}/applications`),
  updateApplication: (jobId, appId, data) => api.put(`/jobs/${jobId}/applications/${appId}`, data),
  saveJob: (jobId) => api.post(`/me/saved-jobs/${jobId}`),
  unsaveJob: (jobId) => api.delete(`/me/saved-jobs/${jobId}`),
  savedJobs: () => api.get('/me/saved-jobs'),
  companyLogo: (company) => api.get('/jobs/company-logo', { params: { company } }),
  logReferral: (jobId) => api.post(`/jobs/${jobId}/referral`),
  getReferrals: (jobId) => api.get(`/jobs/${jobId}/referrals`),
  getCategories: () => api.get('/jobs/categories'),
};

// Events
export const eventsAPI = {
  list: (params) => api.get('/events', { params }),
  get: (id) => api.get(`/events/${id}`),
  create: (data) => api.post('/events', data),
  update: (id, data) => api.put(`/events/${id}`, data),
  delete: (id) => api.delete(`/events/${id}`),
  rsvp: (id) => api.post(`/events/${id}/rsvp`),
  cancelRsvp: (id) => api.delete(`/events/${id}/rsvp`),
  attendees: (id) => api.get(`/events/${id}/attendees`),
};

// Housing
export const housingAPI = {
  list: (params) => api.get('/housing', { params }),
  get: (id) => api.get(`/housing/${id}`),
  create: (data) => api.post('/housing', data),
  update: (id, data) => api.put(`/housing/${id}`, data),
  delete: (id) => api.delete(`/housing/${id}`)
};

// Marketplace
export const marketplaceAPI = {
  list: (params) => api.get('/marketplace', { params }),
  get: (id) => api.get(`/marketplace/${id}`),
  create: (data) => api.post('/marketplace', data),
  update: (id, data) => api.put(`/marketplace/${id}`, data),
  delete: (id) => api.delete(`/marketplace/${id}`),
  vendorStatus: () => api.get('/marketplace/vendor/status'),
  vendorSubscribe: (data) => api.post('/marketplace/vendor/subscribe', data),
  advertiserStatus: () => api.get('/marketplace/advertiser/status'),
  advertiserSubscribe: (data) => api.post('/marketplace/advertiser/subscribe', data),
};

// Dating
export const datingAPI = {
  getProfiles: (params) => api.get('/dating/profiles', { params }),
  createProfile: (data) => api.post('/dating/profile', data),
  like: (profileId) => api.post(`/dating/like/${profileId}`),
  getMatches: () => api.get('/dating/matches')
};

// Messages
export const messagesAPI = {
  getConversations: () => api.get('/messages'),
  getConversation: (userId) => api.get(`/messages/${userId}`),
  send: (receiverId, content, fileData) => api.post('/messages', { receiver_id: receiverId, content, ...fileData })
};

// Notifications
export const notificationsAPI = {
  list: (params) => api.get('/notifications', { params }),
  markRead: (id) => api.put(`/notifications/${id}/read`),
  markAllRead: () => api.put('/notifications/read-all')
};

// Reviews
export const reviewsAPI = {
  list: (providerId) => api.get(`/reviews/${providerId}`),
  create: (data) => api.post('/reviews', data)
};

// Blog
export const blogAPI = {
  list: (params) => api.get('/blog', { params }),
  get: (id) => api.get(`/blog/${id}`)
};

// Feed
export const feedAPI = {
  list: (params) => api.get('/feed', { params }),
  create: (data) => api.post('/feed', data),
  linkPreview: (data) => api.post('/feed/link-preview', data),
  like: (postId) => api.post(`/feed/${postId}/like`),
  // Multi-reaction: type is 'love' | 'like' | 'haha' | 'wow' | 'sad' | 'celebrate'.
  // Posting the same type toggles it off; posting a different one changes it.
  react: (postId, type) => api.post(`/feed/${postId}/react`, { type }),
  // Pass { type } in params to filter to a single reaction (used by per-emoji hover).
  likers: (postId, params) => api.get(`/feed/${postId}/likes`, { params }),
  // Per-comment reactions (lives inside a post)
  reactComment: (postId, commentId, type) => api.post(`/feed/${postId}/comment/${commentId}/react`, { type }),
  commentLikers: (postId, commentId, params) => api.get(`/feed/${postId}/comment/${commentId}/likes`, { params }),
  comment: (postId, data) => api.post(`/feed/${postId}/comment`, typeof data === 'string' ? { content: data } : data),
  editComment: (postId, commentId, data) => api.put(`/feed/${postId}/comment/${commentId}`, data),
  delete: (postId) => api.delete(`/feed/${postId}`),
  update: (postId, data) => api.put(`/feed/${postId}`, data)
};

// Mentorship
export const mentorshipAPI = {
  getMatches: () => api.get('/mentorship/matches'),
  reviewResume: (file) => { const fd = new FormData(); fd.append('file', file); return api.post('/mentorship/resume/review', fd, { headers: { 'Content-Type': 'multipart/form-data' } }); },
  reformatResume: (file) => { const fd = new FormData(); fd.append('file', file); return api.post('/mentorship/resume/reformat', fd, { headers: { 'Content-Type': 'multipart/form-data' } }); },
  generateResume: () => api.post('/mentorship/resume/generate'),
};

// Stats
export const statsAPI = {
  get: () => api.get('/stats')
};

// Payments
export const paymentsAPI = {
  createCheckout: (packageType, itemId, originUrl) => 
    api.post('/payments/checkout', { package_type: packageType, item_id: itemId, origin_url: originUrl }),
  getStatus: (sessionId) => api.get(`/payments/status/${sessionId}`)
};

// Admin
export const adminAPI = {
  getStats: () => api.get('/admin/stats'),
  listUsers: (params) => api.get('/admin/users', { params }),
  updateUserStatus: (userId, data) => api.put(`/admin/users/${userId}/status`, data),
  listProviders: (params) => api.get('/admin/providers', { params }),
  listInvalidProviders: () => api.get('/admin/providers/invalid'),
  deleteProvider: (providerId) => api.delete(`/admin/providers/${providerId}`),
  verifyProvider: (providerId, data) => api.put(`/admin/providers/${providerId}/verify`, data),
  listReports: (params) => api.get('/admin/reports', { params }),
  resolveReport: (reportId, data) => api.put(`/admin/reports/${reportId}`, data),
  getAuditLogs: (params) => api.get('/admin/audit-logs', { params }),
  deleteContent: (contentType, contentId) => api.delete(`/admin/content/${contentType}/${contentId}`),
  updateUserRoles: (userId, roles) => api.put(`/admin/users/${userId}/roles`, { roles }),
  deleteUser: (userId) => api.delete(`/admin/users/${userId}`),
  getRoleCounts: () => api.get('/admin/role-counts'),
  getAnalytics: () => api.get('/admin/analytics'),
  getDetailedAnalytics: () => api.get('/admin/analytics'),
  getDashboardAnalytics: (params) => api.get('/admin/analytics/dashboard', { params }),
  listInvites: () => api.get('/admin/invites'),
  createInvite: (data) => api.post('/admin/invites', data),
  deleteInvite: (inviteId) => api.delete(`/admin/invites/${inviteId}`),
  listFpReferrals: () => api.get('/admin/fp-referrals'),
  createFpReferral: (data) => api.post('/admin/fp-referrals', data),
  resendFpReferral: (referralId) => api.post(`/admin/fp-referrals/${referralId}/resend`),
  deleteFpReferral: (referralId) => api.delete(`/admin/fp-referrals/${referralId}`),
  listPartnerInvites: () => api.get('/admin/partner-invites'),
  createPartnerInvite: (data) => api.post('/admin/partner-invites', data),
  resendPartnerInvite: (inviteId) => api.post(`/admin/partner-invites/${inviteId}/resend`),
  deletePartnerInvite: (inviteId) => api.delete(`/admin/partner-invites/${inviteId}`),
  getModerationSettings: () => api.get('/admin/moderation/settings'),
  updateModerationSettings: (data) => api.put('/admin/moderation/settings', data),
  getModerationQueue: (params) => api.get('/admin/moderation/queue', { params }),
  resolveModerationItem: (itemId, data) => api.put(`/admin/moderation/queue/${itemId}`, data),
  // Trust & Safety
  trustSafetyQueue: (params) => api.get('/admin/trust-safety/queue', { params }),
  trustSafetyStats: () => api.get('/admin/trust-safety/stats'),
  trustSafetyEscalation: (id) => api.get(`/admin/trust-safety/escalation/${id}`),
  trustSafetyAction: (data) => api.post('/admin/trust-safety/action', data),
  trustSafetyRecompute: () => api.post('/admin/trust-safety/recompute'),
  // Radio Stations
  radioStations: () => api.get('/admin/radio-stations'),
  createRadioStation: (data) => api.post('/admin/radio-stations', data),
  updateRadioStation: (id, data) => api.put(`/admin/radio-stations/${id}`, data),
  deleteRadioStation: (id) => api.delete(`/admin/radio-stations/${id}`),
  seedRadioStations: () => api.post('/admin/radio-stations/seed'),
  // Social Queue
  listSocialQueue: (params) => api.get('/admin/social-queue', { params }),
  createSocialPost: (data) => api.post('/admin/social-queue', data),
  updateSocialPost: (id, data) => api.put(`/admin/social-queue/${id}`, data),
  deleteSocialPost: (id) => api.delete(`/admin/social-queue/${id}`),
  updateSocialPostStatus: (id, status) => api.patch(`/admin/social-queue/${id}/status`, { status }),
  getPendingSocialPosts: () => api.get('/admin/social-queue/pending'),
  getSocialOgPreview: (url) => api.get('/admin/social-queue/og-preview', { params: { url } }),
  uploadSocialImage: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/admin/upload/social-image', fd);
  },
  generateSocialImage: (text, template = 'auto') =>
    api.post('/admin/social-image/generate', { text, template }),
  // Newsletter / Monday Digest
  newsletterData: () => api.get('/admin/newsletter/data'),
  // Returns the rendered HTML as a string — iframe via srcDoc since the endpoint requires auth.
  newsletterPreview: (params = {}) =>
    api.get('/admin/newsletter/preview', { params, responseType: 'text', transformResponse: [(v) => v] }).then((r) => r.data),
  newsletterTestSend: (data) => api.post('/admin/newsletter/test-send', data),
  newsletterSync: (data = {}) => api.post('/admin/newsletter/sync-contacts', data),
  newsletterSend: (data) => api.post('/admin/newsletter/send', data),
  newsletterBroadcasts: () => api.get('/admin/newsletter/broadcasts'),
};

// Public radio stations (for HBCUMedia page)
export const radioAPI = {
  listActive: () => api.get('/radio-stations'),
};

// HBCU Creators
export const creatorsAPI = {
  list: (params) => api.get('/hbcu-creators', { params }),
  youtubeFeed: (channelId) => api.get(`/youtube-feed/${channelId}`),
  submitCreator: (data) => api.post('/hbcu-creators/submit', data),
  // Podcasts
  getCuratedPodcasts: () => api.get('/curated-podcasts'),
  submitPodcast: (data) => api.post('/podcast-submissions', data),
  // Admin
  getCreatorSubmissions: (status) => api.get('/admin/creator-submissions', { params: { status } }),
  approveCreator: (id) => api.put(`/admin/creator-submissions/${id}/approve`),
  rejectCreator: (id) => api.put(`/admin/creator-submissions/${id}/reject`),
  getPodcastSubmissions: (status) => api.get('/admin/podcast-submissions', { params: { status } }),
  approvePodcast: (id) => api.put(`/admin/podcast-submissions/${id}/approve`),
  rejectPodcast: (id) => api.put(`/admin/podcast-submissions/${id}/reject`),
};


// Alumni Association
export const alumniAssociationAPI = {
  getDashboard: () => api.get('/alumni-association/dashboard'),
  getMembers: (params) => api.get('/alumni-association/members', { params }),
  announce: (data) => api.post('/alumni-association/announce', data),
  getEvents: (params) => api.get('/alumni-association/events', { params }),
  getAnalytics: () => api.get('/alumni-association/analytics'),
  createCampaign: (data) => api.post('/alumni-association/campaigns', data),
  getCampaigns: () => api.get('/alumni-association/campaigns'),
  updateCampaign: (id, data) => api.put(`/alumni-association/campaigns/${id}`, data),
  getCampaignDonors: (id) => api.get(`/alumni-association/campaigns/${id}/donors`),
  recommendTalent: (data) => api.post('/alumni-association/recommend', data),
  createMeeting: (data) => api.post('/alumni-association/meetings', data),
  getMeetings: () => api.get('/alumni-association/meetings'),
};

// Fundraising (public)
export const fundraisingAPI = {
  getCampaigns: (params) => api.get('/fundraising/campaigns', { params }),
  getCampaign: (id) => api.get(`/fundraising/campaigns/${id}`),
  donate: (id, data) => api.post(`/fundraising/campaigns/${id}/donate`, data),
};

// Recruiter
export const recruiterAPI = {
  getDashboard: () => api.get('/recruiter/dashboard'),
  searchTalent: (params) => api.get('/recruiter/talent', { params }),
  aiMatch: (userId, jobDescription) => api.post(`/recruiter/talent/${userId}/ai-match`, { job_description: jobDescription }),
  getJobs: (params) => api.get('/recruiter/jobs', { params }),
  getPipeline: (params) => api.get('/recruiter/pipeline', { params }),
  updatePipeline: (applicationId, data) => api.put(`/recruiter/pipeline/${applicationId}`, data),
  addToPipeline: (data) => api.post('/recruiter/pipeline', data),
  getSettings: () => api.get('/recruiter/settings'),
  updateSettings: (data) => api.put('/recruiter/settings', data),
  pushToGreenhouse: (applicationId) => api.post(`/recruiter/greenhouse/push/${applicationId}`),
};

// Sponsor / Advertiser
export const sponsorAPI = {
  getDashboard: () => api.get('/sponsors/dashboard'),
  createCampaign: (data) => api.post('/sponsors/campaigns', data),
  updateCampaign: (campaignId, data) => api.put(`/sponsors/campaigns/${campaignId}`, data),
  getCampaignAnalytics: (campaignId) => api.get(`/sponsors/campaigns/${campaignId}/analytics`),
  getAds: (params) => api.get('/sponsors/ads', { params }),
  recordClick: (campaignId) => api.post(`/sponsors/ads/${campaignId}/click`)
};

// Reports
export const reportsAPI = {
  create: (data) => api.post('/report', data)
};

// Invites (public token-based lookup + accept)
export const invitesAPI = {
  lookup: (token) => api.get(`/invites/${token}`),
  accept: (token, userId) => api.post(`/invites/${token}/accept`, { user_id: userId }),
};

// Founding Provider referrals (public token lookup, used by /founding-provider)
export const fpReferralsAPI = {
  lookup: (token) => api.get(`/fp-referrals/${token}`),
};

// Partner invites (public token lookup, used by /role-signup)
export const partnerInvitesAPI = {
  lookup: (token) => api.get(`/partner-invites/${token}`),
};

// Uploads
export const uploadAPI = {
  profilePhoto: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/upload/profile-photo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  coverPhoto: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/upload/cover-photo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  videoIntro: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/upload/video-intro', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  postImage: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/upload/post-image', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  marketplaceImage: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/upload/marketplace-image', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  jobLogo: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/upload/job-logo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  messageFile: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/upload/message-file', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  }
};

// Push Notifications
export const pushAPI = {
  getVapidKey: () => api.get('/push/vapid-public-key'),
  subscribe: (subscription) => api.post('/push/subscribe', { subscription }),
  unsubscribe: () => api.post('/push/unsubscribe')
};

// Video
export const videoAPI = {
  createRoom: (data) => api.post('/video/create-room', data || {}),
  getRoom: (roomId) => api.get(`/video/room/${roomId}`),
  joinRoom: (roomId) => api.post(`/video/room/${roomId}/join`),
  leaveRoom: (roomId) => api.post(`/video/room/${roomId}/leave`),
};

// Bookings
export const bookingsAPI = {
  getProviderAvailability: (providerId) => api.get(`/providers/${providerId}/availability`),
  updateAvailability: (providerId, data) => api.put(`/providers/${providerId}/availability`, data),
  create: (data) => api.post('/bookings', data),
  list: () => api.get('/bookings'),
  update: (bookingId, data) => api.put(`/bookings/${bookingId}`, data)
};

// Groups
export const groupsAPI = {
  list: (params) => api.get('/groups', { params }),
  myGroups: () => api.get('/groups/my'),
  create: (data) => api.post('/groups', data),
  join: (groupId) => api.post(`/groups/${groupId}/join`),
  leave: (groupId) => api.post(`/groups/${groupId}/leave`),
  getMessages: (groupId, params) => api.get(`/groups/${groupId}/messages`, { params }),
  sendMessage: (groupId, data) => api.post(`/groups/${groupId}/messages`, data)
};

export const analyticsAPI = {
  trackPageview: (data) => api.post('/analytics/pageview', data),
};

// Share URLs — uses the frontend domain so shared links show gogradlink.com
// Firebase Hosting redirects /share/* to the backend for OG tag rendering
export const getShareUrl = (type, id) => `${window.location.origin}/share/${type}/${id}`;
// Usage: getShareUrl('job', 'job_xxx'), getShareUrl('event', 'evt_xxx'), etc.
// Types: job, event, provider, housing, marketplace, post

export default api;
